import './_app.scss';
import '@/styles/globals.scss';

import { BroadcastChannelProvider } from '@chewy/chirp-app-helpers';
import { KibApplicationManager } from '@chewy/kib-application-react';
import { InternationalMarket } from '@chewy/market-handler';
import {
  DehydratedState,
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { NextPage } from 'next';
import type { AppProps } from 'next/app';
import React, { ReactElement, ReactNode, useState } from 'react';
import { I18nextProvider } from 'react-i18next';

import CsrHeader from '@/components/common/CsrHeader';
import { NmaBridge } from '@/components/common/NmaBridge/NmaBridge';
import { queryClientConfig } from '@/constants/query-options';
import AnalyticsProvider from '@/providers/AnalyticsProvider';
import { AnimationProvider } from '@/providers/AnimationProvider';
import { AuthProvider } from '@/providers/AuthProvider';
import { ChewyPortalProvider } from '@/providers/ChewyPortalProvider';
import { FeatureProvider } from '@/providers/feature-provider';
import { LoyaltyIosFetchBridgeProvider } from '@/providers/IosFetchBridgeProvider';
import { LoyaltyThemeProvider } from '@/providers/LoyaltyThemeProvider';
import { MarketProvider } from '@/providers/MarketProvider';
import { TelemedicineEligibilityProvider } from '@/providers/TelemedicineEligibilityProvider';
import { UserContextData } from '@/providers/types';
import { UserAgentProvider } from '@/providers/UserAgentProvider';
import type { HeaderFooterComponentsResponse } from '@/services/header-footer/header-footer';
import { LoyaltyPageData } from '@/utils/ssr';
import { safeTimeZone } from '@/utils/timezone';

import { createI18nInstance } from '../i18n';

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout<P = any> = AppProps<P> & {
  Component: NextPageWithLayout<P>;
};

export interface MarketProps {
  isUSMarket: boolean;
  market: string;
  basePath: string;
  usMarketCode: string;
  siteId: string;
}

export type LoyaltyAppProps = {
  headerFooter: HeaderFooterComponentsResponse;
  userContextData: UserContextData;
  dehydratedState: DehydratedState;
  showWebExperience: boolean;
  athenaValues?: { [key: string]: any } | undefined;
  chewyPortalEnabled?: boolean;
  environment: string;
  marketProps: MarketProps;
  userAgent: string;
  pageData: LoyaltyPageData;
  timeZone?: string;
  telemedicineEligible?: boolean;
};

const DEFAULT_MARKET_PROPS: MarketProps = {
  isUSMarket: true,
  market: InternationalMarket.enUS,
  basePath: '',
  usMarketCode: InternationalMarket.enUS,
  siteId: '10',
};

export default function App({ Component, pageProps }: AppPropsWithLayout<LoyaltyAppProps>) {
  const [queryClient] = useState(() => new QueryClient(queryClientConfig));
  const userContext = pageProps?.userContextData;
  const showWebExperience = pageProps?.showWebExperience;
  const userAgent = pageProps?.userAgent ?? '';
  const isNMA = !showWebExperience;
  const getLayout = Component.getLayout ?? (page => page);
  const timeZone = pageProps?.timeZone ?? safeTimeZone();

  const marketProps = pageProps?.marketProps ?? DEFAULT_MARKET_PROPS;
  const market = marketProps.market;
  const i18n = createI18nInstance(market);

  const pageData = pageProps?.pageData;

  return (
    <BroadcastChannelProvider enabled={isNMA}>
      <I18nextProvider i18n={i18n}>
        <MarketProvider market={marketProps}>
          <QueryClientProvider client={queryClient}>
            <HydrationBoundary state={pageProps?.dehydratedState}>
              <UserAgentProvider userAgent={userAgent}>
                <AnalyticsProvider
                  environment={pageProps?.environment}
                  market={market}
                  pageData={pageData}
                  telemedicineEligible={pageProps?.telemedicineEligible}
                  userContext={userContext}
                >
                  <AuthProvider market={market} userContext={userContext}>
                    <FeatureProvider athenaValues={pageProps?.athenaValues}>
                      <TelemedicineEligibilityProvider
                        eligible={pageProps?.telemedicineEligible ?? false}
                      >
                        <LoyaltyIosFetchBridgeProvider isNativeMobileApp={isNMA}>
                          <ChewyPortalProvider enabled={pageProps?.chewyPortalEnabled ?? false}>
                            <LoyaltyThemeProvider timeZone={timeZone}>
                              <AnimationProvider>
                                <KibApplicationManager>
                                  <main>
                                    <CsrHeader />
                                    {!showWebExperience && <NmaBridge />}
                                    {getLayout(<Component {...pageProps} />)}
                                  </main>
                                </KibApplicationManager>
                              </AnimationProvider>
                            </LoyaltyThemeProvider>
                          </ChewyPortalProvider>
                        </LoyaltyIosFetchBridgeProvider>
                      </TelemedicineEligibilityProvider>
                    </FeatureProvider>
                  </AuthProvider>
                </AnalyticsProvider>
              </UserAgentProvider>
            </HydrationBoundary>
          </QueryClientProvider>
        </MarketProvider>
      </I18nextProvider>
    </BroadcastChannelProvider>
  );
}

if (typeof window === 'undefined') {
  /**
   * Suppress useLayoutEffect warnings coming from CDS components.
   * https://chewyinc.atlassian.net/browse/CDS-5015
   *
   * ```
   * Warning: useLayoutEffect does nothing on the server, because
   * its effect cannot be encoded into the server renderer's output format.
   * This will lead to a mismatch between the initial, non-hydrated UI and
   * the intended UI. To avoid this, useLayoutEffect should only be used in
   * components that render exclusively on the client.
   * See https://reactjs.org/link/uselayouteffect-ssr for common fixes.
   * ```
   */
  React.useLayoutEffect = React.useEffect;
}
